import React from 'react';
import { Box, Typography } from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import BlockIcon from '@mui/icons-material/Block';
import ChecklistIcon from '@mui/icons-material/Checklist';

export interface StepContent {
  icon: React.ReactNode;
  title: string;
  description: string;
  tip: string;
}

// Define the onboarding steps content
export const ONBOARDING_STEPS: StepContent[] = [
  {
    icon: <ScheduleIcon sx={{ fontSize: 64, color: '#662C8B' }} />,
    title: 'Store Hours',
    description:
      'Set your regular store hours, create temporary closures for holidays, or modify hours for special events.',
    tip: 'Tip: Use "split hours" to close mid-day (e.g., 11am-2pm, 5pm-10pm)',
  },
  {
    icon: <WifiTetheringIcon sx={{ fontSize: 64, color: '#662C8B' }} />,
    title: 'Channel Management',
    description:
      'Turn delivery channels on or off with a single tap. Control which ordering platforms are active for your store.',
    tip: 'Tip: Add a reason when turning off a channel so customers know why',
  },
  {
    icon: <BlockIcon sx={{ fontSize: 64, color: '#662C8B' }} />,
    title: 'Snooze Items (86)',
    description:
      'Temporarily remove items from your menu when you run out. Items automatically return after the snooze period ends.',
    tip: 'Tip: Set a snooze duration so items come back automatically',
  },
  {
    icon: <ChecklistIcon sx={{ fontSize: 64, color: '#662C8B' }} />,
    title: 'Bulk Operations',
    description:
      'Select multiple stores to update hours, channels, or snooze items across all of them at once.',
    tip: 'Tip: Tap the checkbox next to each store, then choose your action',
  },
];

interface OnboardingStepProps {
  stepIndex: number;
}

export const OnboardingStep: React.FC<OnboardingStepProps> = ({ stepIndex }) => {
  const step = ONBOARDING_STEPS[stepIndex];

  if (!step) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 3,
        py: 4,
        height: '100%',
        minHeight: 300,
      }}
    >
      {/* Icon */}
      <Box sx={{ mb: 3 }}>{step.icon}</Box>

      {/* Title */}
      <Typography
        variant="h5"
        component="h2"
        sx={{
          fontWeight: 600,
          color: '#662C8B',
          mb: 2,
        }}
      >
        {step.title}
      </Typography>

      {/* Description */}
      <Typography
        variant="body1"
        sx={{
          color: 'text.secondary',
          mb: 3,
          maxWidth: 320,
          lineHeight: 1.6,
        }}
      >
        {step.description}
      </Typography>

      {/* Tip */}
      <Box
        sx={{
          bgcolor: 'rgba(102, 44, 139, 0.08)',
          borderRadius: 2,
          px: 2,
          py: 1.5,
          maxWidth: 320,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: '#662C8B',
            fontStyle: 'italic',
          }}
        >
          {step.tip}
        </Typography>
      </Box>
    </Box>
  );
};

export default OnboardingStep;
